import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService } from 'src/app/services/auth.service';
import { FormControl, FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ACTIVITY_ACTION_LABEL_MAP } from './activity-log-action-labels.constant';

@Component({
  selector: 'app-activity-log',
  templateUrl: './activity-log.component.html',
  styleUrls: ['./activity-log.component.scss'],
  
})
export class ActivityLogComponent implements OnInit {

  currentUser: any = undefined;

  organisations: Array<{ id: string; organisationName: string }> = [];
  filteredUsers: Array<any> = [];

  // Track selected values for edit detection
  selectedUserId: string = '';
  selectedUserName: string = '';
  selectedOrgId: string = '';

  get displayedColumns(): string[] {
    const cols = ['name', 'email', 'date', 'latestAction'];
    if (this.isMainSuperUser) {
      cols.splice(2, 0, 'organisationName');
    }
    return cols;
  }

  timezone: string = 'UTC';

  // helper: true when Local Time display is active
  get isConvertTimeOn(): boolean {
    return this.timezone === 'Local Time';
  }

  dataSource = new MatTableDataSource<any>([]);

  todayDate: Date = new Date();

  range = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });

  orgSearchControl:any = new FormControl('');
  userSearchControl:any = new FormControl('');

  stDate: Date | null = null;
  endDate: Date | null = null;

  organisationId: string = '';
  userId: string = '';

  pageIndex: number = 0;
  totalCount: number = 0;

  filteredData: any[] = [];
  tableDataLength: number = 0;

  isLoading: boolean = false;
  isMainSuperUser: boolean = false;

  @ViewChild('tableScrollContainer') tableScrollContainer: ElementRef;

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private userService: UserService,
    private utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.userValue;
    this.userService.personalInfo(this.currentUser?.id).subscribe(async (res: any) => {
      let userDetails = await res;
      this.isMainSuperUser = userDetails?.userType === 'Super user' && !userDetails?.isSubAccount && userDetails?.user_plan == 4 ? true : false;
    });
    this.getActivityLogs();
  }

  datewiseOrder(type: string, e: any) {

    if (type === 'start') this.stDate = e.value;
    if (type === 'end') this.endDate = e.value;

    if (this.stDate && this.endDate) {

      this.pageIndex = 0;
      this.filteredData = [];
      this.totalCount = 0;
      this.tableDataLength = 0;

      this.getActivityLogs();
    }
  }

  private buildActivityLogRequest(pageIndex: number) {

    if (this.stDate && this.endDate) {

      const startDate = moment(this.stDate)
        .utcOffset(0, true)
        .format();

      const endDate = moment(this.endDate)
        .utcOffset(0, true)
        .add(86399, 'seconds')
        .format();

      return {
        organisationId: this.organisationId,
        userId: this.userId,
        startDate,
        endDate,
        pageIndex
      };
    }

    return {
      organisationId: this.organisationId,
      userId: this.userId,
      startDate: '',
      endDate: '',
      pageIndex
    };
  }

  getActionLabel(action: string): string {
    return ACTIVITY_ACTION_LABEL_MAP[action]?.label || action;
  }

  getActivityLogs() {

    if (this.isLoading) return;

    this.isLoading = true;

    const params = this.buildActivityLogRequest(this.pageIndex);
    const userId = this.currentUser?.id || '';

    this.apiService.getActivityLogs(userId, params).subscribe(
      (result: any) => {

        this.totalCount = result?.total || 0;

        const rows = (result?.data || []).map((x: any) => {

          x.name = x.fullName || '';
          x.date = x.dateTime || '';
          x.organisationName = x.organisationName || '';
          const actionKey = x.latestAction || x.action || '';
          x.latestAction = this.getActionLabel(actionKey);
          x.products = x.metadata?.product_payload || [];
          x.type = this.getType(actionKey, x);

          const actionMeta = this.getActionMeta(actionKey);
          x.actionIcon = actionMeta.icon;
          x.actionClass = actionMeta.className;
          return x;
        });

        // apply timezone handling to newly fetched rows so lazy-loaded data
        // matches the current display mode (UTC or Local Time)
        this.applyTimezoneToRows(rows);

        this.filteredData = this.filteredData.concat(rows);
        this.tableDataLength = this.filteredData.length;

        this.dataSource = new MatTableDataSource(this.filteredData);

        this.isLoading = false;

        setTimeout(() => {

          const target = this.tableScrollContainer?.nativeElement as HTMLElement;
          if (!target) return;

          if (
            this.filteredData.length < this.totalCount &&
            target.scrollHeight <= target.clientHeight
          ) {
            this.pageIndex++;
            this.getActivityLogs();
          }

        }, 0);

      },
      () => {

        this.isLoading = false;
        this.totalCount = 0;
        this.filteredData = [];
        this.tableDataLength = 0;
        this.dataSource = new MatTableDataSource<any>([]);

      }
    );
  }

  // Reuse existing conversion logic for rows appended via lazy load.
  private applyTimezoneToRows(rows: any[]) {
    if (!rows || !rows.length) return;

    if (!this.isConvertTimeOn) {
      // ensure row.date is Date or ISO string for DatePipe when showing UTC
      for (const row of rows) {
        if (!(row.date instanceof Date)) {
          const original = row.dateTime || row.date;
          if (typeof original === 'string') {
            const parsed = new Date(original);
            if (!isNaN(parsed.getTime())) {
              row.date = parsed;
            }
          }
        }
      }
      return;
    }

    // conversion active: use convertDateAndTimes to set _displayDate
    for (const row of rows) {
      try {
        const converted = this.convertDateAndTimes(row);
        row._displayDate = converted?.dateTime || '';
      } catch (e) {
        row._displayDate = row.dateTime || row.date || '';
      }
      // also ensure row.date is a Date for DatePipe fallback
      if (!(row.date instanceof Date)) {
        const original = row.dateTime || row.date;
        if (typeof original === 'string') {
          const parsed = new Date(original);
          if (!isNaN(parsed.getTime())) {
            row.date = parsed;
          }
        }
      }
    }
  }

  resetTable() {
    this.pageIndex = 0;
    this.filteredData = [];
    this.totalCount = 0;
    this.tableDataLength = 0;
  }

  loadMoreData() {

    if (this.isLoading) return;

    if (this.filteredData.length < this.totalCount) {
      this.pageIndex++;
      this.getActivityLogs();
    }
  }

  onUserSearchInput(event: any) {

    const value = typeof event === 'string' ? event : event?.target?.value ?? '';

    const search = value.trim();

    if (search.length === 0) {
      this.userId = '';
      this.selectedUserId = '';
      this.selectedUserName = '';
      this.filteredUsers = [];

      this.resetTable();
      this.getActivityLogs();
      return;
    }

    // Ignore until 3 characters
    if (search.length < 3) {
      this.filteredUsers = [];
      return;
    }

    // If user edits after selection → clear filter
    if (this.selectedUserId && search !== this.selectedUserName) {
      this.userId = '';
      this.selectedUserId = '';
    }

    const currentUserId = this.currentUser?.id || '';

    this.apiService
      .getActivityUsers(currentUserId, search)
      .subscribe((res: any) => {
        this.filteredUsers = res?.data || [];
      });
  }

  // Called when user selects an option from dropdown
  onUserSelect(user: any) {

    if (user && user.userId) {

      this.userId = user.userId;
      this.selectedUserId = user.userId;
      this.selectedUserName = user.fullName;

      this.userSearchControl.setValue(user, { emitEvent: false });

      this.resetTable();
      this.getActivityLogs();
    }
  }

  onOrgSearchInput(event: any) {

    const value = typeof event === 'string' ? event : event?.target?.value ?? '';

    const search = value.trim();

    if (search.length === 0) {
      this.organisationId = '';
      this.selectedOrgId = '';
      this.organisations = [];

      this.resetTable();
      this.getActivityLogs();
      return;
    }

    if (search.length < 3) {
      this.organisations = [];
      return;
    }

    
    const selectedName = typeof this.orgSearchControl.value === 'object' ? this.orgSearchControl.value.organisationName : '';

    if (this.selectedOrgId && search !== selectedName) {
      this.organisationId = '';
      this.selectedOrgId = '';
    }

    const currentUserId = this.currentUser?.id || '';

    this.apiService
      .getActivityOrganisations(currentUserId, search)
      .subscribe((res: any) => {
        this.organisations = res?.data || [];
      });
  }

  // Called when user selects an option from dropdown
  onOrgSelect(org: any) {
    if (org && org.id) {
      this.organisationId = org.id;
      this.selectedOrgId = org.id;
      this.orgSearchControl.setValue(org, { emitEvent: false });
      this.resetTable();
      this.getActivityLogs();
    }
  }

  // Display function for user (shows selected user name only, not the search text)
  displayUser = (user: any) => {
    if (!user || typeof user === 'string') return '';
    return `${user.fullName || ''} - ${user.email || ''}`;
  };

  // Display function for organisation (shows selected org name only, not the search text)
  displayOrg = (org: any) => {
    if (!org || typeof org === 'string') return '';
    return org.organisationName || '';
  };

  // Helper: get search input text value for hint display
  getUserSearchInputValue(): string {
    const val = this.userSearchControl.value;
    return (typeof val === 'string' ? val : '');
  }

  getOrgSearchInputValue(): string {
    const val = this.orgSearchControl.value;
    return (typeof val === 'string' ? val : '');
  }

  convertTime(event: MouseEvent) {
    event.stopPropagation();
    this.timezone = this.timezone === 'UTC' ? 'Local Time' : 'UTC';
    
    for (const row of this.filteredData) {
      const convertedDates = this.convertDateAndTimes(row);
      // store a pre-formatted display string for Local Time to avoid DatePipe errors
      row._displayDate = convertedDates.dateTime;
      // ensure `row.date` remains a Date or ISO string for use with DatePipe when timezone is UTC
      if (!(row.date instanceof Date)) {
        const original = row.dateTime || row.date;
        if (typeof original === 'string') {
          const parsed = new Date(original);
          if (!isNaN(parsed.getTime())) {
            row.date = parsed;
          }
        }
      }
    }

    this.dataSource = new MatTableDataSource(this.filteredData);
  }

  convertDateAndTimes({ dateTime }: any) {
    return {
      dateTime: this.formatDate(dateTime)
    };
  }

  private formatDate(dateTime: string) {
    if (!dateTime) return null;

    return this.timezone === 'Local Time'
      ? this.utilsService.convertUtcToLocal(dateTime, this.currentUser)
      : dateTime;
  }
  
  clearDateRange(event: Event) {
    event.stopPropagation();
    this.range.reset();
    this.stDate = null;
    this.endDate = null;
    this.pageIndex = 0;
    this.filteredData = [];
    this.totalCount = 0;
    this.tableDataLength = 0;
    this.getActivityLogs();
  }

  // Clear all filters and reload initial data
  clearFilter(): void {
    try {
      if (this.orgSearchControl) this.orgSearchControl.setValue('');
      if (this.userSearchControl) this.userSearchControl.setValue('');
      if (this.range) this.range.reset();
    } catch (e) { }

    this.selectedOrgId = '';
    this.selectedUserId = '';
    this.organisationId = '';
    this.userId = '';

    this.filteredUsers = [];

    // reset table and reload default data
    this.filteredData = [];
    this.dataSource = new MatTableDataSource(this.filteredData);
    this.pageIndex = 0;
    this.getActivityLogs();
  }

  // Legacy method for backwards compatibility (if called from template)
  filterUsers(value: any) {
    if (value && value.userId !== undefined) {
      this.onUserSelect(value);
    } else {
      this.onUserSearchInput(value);
    }
  }

  // Legacy method for backwards compatibility (if called from template)
  filterOrganisations(value: any) {
    if (value && value.id !== undefined) {
      this.onOrgSelect(value);
    } else {
      this.onOrgSearchInput(value);
    }
  }
  
  get hasActiveFilters(): boolean {
    return !!(this.userId || this.organisationId || this.stDate || this.endDate);
  }
  
  getActionMeta(action: string): {icon: string;className: string;} {

    const value = (action || '').toLowerCase();

    if (value.includes('created') || value.includes('added')) {
      return {
        icon: 'add_circle',
        className: 'success-action'
      };
    }

    if (value.includes('ordered')) {
      return {
        icon: 'shopping_cart',
        className: 'ordered-action'
      };
    }

    if (value.includes('cancel')) {
      return {
        icon: 'cancel',
        className: 'cancel-action'
      };
    }

    if (value.includes('download')) {
      return {
        icon: 'download',
        className: 'download-action'
      };
    }

    if (value.includes('return')) {
      return {
        icon: 'keyboard_return',
        className: 'return-action'
      };
    }

    if (value.includes('view')) {
      return {
        icon: 'visibility',
        className: 'view-action'
      };
    }

    if (value.includes('delete')) {
      return {
        icon: 'delete',
        className: 'delete-action'
      };
    }

    if (value.includes('budget')) {

      if (value.includes('saved')) {
        return {
          icon: 'add_card',
          className: 'success-action'
        };
      }

      if (value.includes('tasked')) {
        return {
          icon: 'credit_score',
          className: 'ordered-action'
        };
      }
    }

    return {
      icon: 'info',
      className: 'default-action'
    };
  }

  private getType(action: string, row: any): string {

    const typeSource = ACTIVITY_ACTION_LABEL_MAP[action]?.typeSource;

    switch (typeSource) {

      case 'feed':
        return row.metadata?.feedDetails?.[0]?.feedName || '';

      case 'product':
        return row.metadata?.product_type || row.metadata?.product || row.metadata?.products ||'';
      
      case 'question':
        return row.metadata?.question_name || '';

      case 'sensor':
        return this.currentUser?.obfuscated ? (row.obfuscated_name || row.metadata?.obfuscated_name || '') : (row.sensor_name || row.metadata?.sensor_name || '');

      default:
        return '';
    }
  }

  getProductText(products): any {
    if (!products?.length) {
      return '';
    }

    return `${ACTIVITY_ACTION_LABEL_MAP[products[0].product_action]?.label || ''} - ${products
      .map(p => p.product_type)
      .join(', ')}`;
  }
}
